import { useState } from 'react';
import { AlertCircle, Loader2, Send } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { GALLERY_COMMENTS } from '@/constants/testIds';

const CommentItem = ({ comment: c }) => (
  <div data-testid={GALLERY_COMMENTS.item(c.id)} className="rounded-lg border border-[hsl(var(--lxra-border))] bg-[#141416] p-2.5 text-left">
    <p className="text-xs text-[hsl(var(--lxra-fg))] leading-relaxed">{c.body}</p>
    <div className="mt-1 flex items-center gap-2">
      {c.is_revision_request && (
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] text-amber-400">
          <AlertCircle className="h-3 w-3" /> Revision requested
        </span>
      )}
      {c.resolved && <span className="text-[10px] uppercase tracking-[0.08em] text-emerald-400">Resolved</span>}
    </div>
    {c.photographer_reply && (
      <div data-testid={GALLERY_COMMENTS.reply(c.id)} className="mt-2 rounded-md border-l-2 border-[hsl(var(--lxra-gold))] bg-[#0F0F10] px-2.5 py-2">
        <p className="text-[10px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-gold))]">Photographer replied</p>
        <p className="text-xs text-[hsl(var(--lxra-fg))] leading-relaxed mt-0.5 whitespace-pre-wrap">{c.photographer_reply}</p>
      </div>
    )}
  </div>
);

/** Client notes / revision requests for the current lightbox photo. */
export const LightboxNotesPanel = ({ image, comments, onAddComment }) => {
  const [body, setBody] = useState('');
  const [isRevision, setIsRevision] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!body.trim() || submitting) return;
    setSubmitting(true);
    try {
      await onAddComment(image, body.trim(), isRevision);
      setBody('');
      setIsRevision(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={GALLERY_COMMENTS.panel} className="w-[min(420px,90vw)] max-h-[40vh] overflow-y-auto rounded-2xl bg-[#0F0F10]/95 backdrop-blur border border-[hsl(var(--lxra-border))] p-4">
      {comments.length > 0 && (
        <div data-testid={GALLERY_COMMENTS.list} className="space-y-2 mb-3">
          {comments.map((c) => <CommentItem key={c.id} comment={c} />)}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Leave a note for your photographer..."
          data-testid={GALLERY_COMMENTS.textarea}
          className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] text-sm min-h-[64px] resize-none"
        />
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs text-[hsl(var(--lxra-muted))] cursor-pointer">
            <Checkbox
              checked={isRevision}
              onCheckedChange={(v) => setIsRevision(Boolean(v))}
              data-testid={GALLERY_COMMENTS.revisionCheckbox}
              className="border-[hsl(var(--lxra-border))] data-[state=checked]:bg-[hsl(var(--lxra-gold))] data-[state=checked]:border-[hsl(var(--lxra-gold))]"
            />
            Request a revision
          </label>
          <Button type="submit" size="sm" disabled={!body.trim() || submitting} data-testid={GALLERY_COMMENTS.submitButton} className="h-8 px-3 rounded-full bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs disabled:opacity-50">
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </form>
    </div>
  );
};
